import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { H3 } from "../../../../AbstractElements";
import axios from "axios";

interface SummaryRow {
  recruiter: string;
  new_candidate_screen: number;
  consultant_relations_checkin: number;
  internal_submissions: number;
  client_submissions: number;
  client_interviews: number;
  interviewing: number;
  total_placements: number;
  placement_backfill: number;
  placement_extension: number;
  placement_new_headcount: number;
  placement_ace: number;
  rolling_headcount: number;
  total_headcount: number;
}

interface WeeklyRow {
  recruiter: string;
  week_start: string;
  week_end: string;
  week_label: string;
  new_candidate_screen: number;
  consultant_relations_checkin: number;
  internal_submissions: number;
  client_submissions: number;
  client_interviews: number;
  interviewing: number;
  total_placements: number;
  placement_backfill: number;
  placement_extension: number;
  placement_new_headcount: number;
  placement_ace: number;
}

interface HeadcountRow {
  recruiter: string;
  first_name: string;
  last_name: string;
  client_name: string | null;
  placement_id: number | string;
  job_title: string | null;
  startDate: string | null;
  endDate: string | null;
  last_timesheet_month: string | null;
  last_consultant_relation: string | null;
}

interface TotalHeadcountRow {
  recruiter: string;
  first_name: string;
  last_name: string;
  last_consultant_relation: string | null;
}

interface DrilldownRow {
  recruiter: string;
  event_date: string | null;
  note_id?: number | string;
  submission_id?: number | string;
  appointment_id?: number | string;
  placement_id?: number | string;
  candidate_id?: number | string;
  first_name?: string | null;
  last_name?: string | null;
  job_order_id?: number | string;
  job_title?: string | null;
  client_name?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  contracted?: number | null;
  comments?: string | null;
  action?: string | null;
}

type DrilldownMetric =
  | "new_candidate_screen"
  | "consultant_relations_checkin"
  | "internal_submissions"
  | "client_submissions"
  | "client_interviews"
  | "interviewing"
  | "placements"
  | "placement_extension"
  | "placement_new_headcount"
  | "placement_ace";

const DEFAULT_START_DATE = "2026-01-01";

const todayAsInputValue = (): string => {
  return new Date().toISOString().slice(0, 10);
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  // Parse YYYY-MM-DD as local date to avoid UTC-midnight timezone rollback
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  let date: Date;
  if (parts) {
    date = new Date(
      parseInt(parts[1], 10),
      parseInt(parts[2], 10) - 1,
      parseInt(parts[3], 10),
    );
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMetricLabel = (metric: DrilldownMetric): string => {
  switch (metric) {
    case "new_candidate_screen":
      return "New Candidate Screen";
    case "consultant_relations_checkin":
      return "Consultant Relations Check In";
    case "internal_submissions":
      return "Internal Submissions";
    case "client_submissions":
      return "Client Submissions";
    case "client_interviews":
      return "Client Interviews";
    case "interviewing":
      return "Interviewing";
    case "placements":
      return "Placements";
    case "placement_extension":
      return "Extensions";
    case "placement_ace":
      return "ACE";
    case "placement_new_headcount":
      return "New Headcount";
    default:
      return metric;
  }
};

const toInt = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const normalizeSummaryRow = (row: any): SummaryRow => ({
  recruiter: row?.recruiter ?? "",
  new_candidate_screen: toInt(row?.new_candidate_screen),
  consultant_relations_checkin: toInt(row?.consultant_relations_checkin),
  internal_submissions: toInt(row?.internal_submissions),
  client_submissions: toInt(row?.client_submissions),
  client_interviews: toInt(row?.client_interviews),
  interviewing: toInt(row?.interviewing),
  total_placements: toInt(row?.total_placements),
  placement_backfill: toInt(row?.placement_backfill),
  placement_extension: toInt(row?.placement_extension),
  placement_new_headcount: toInt(row?.placement_new_headcount),
  rolling_headcount: toInt(row?.rolling_headcount),
  placement_ace: toInt(row?.placement_ace),
  total_headcount: toInt(row?.total_headcount),
});

const normalizeWeeklyRow = (row: any): WeeklyRow => ({
  recruiter: row?.recruiter ?? "",
  week_start: row?.week_start ?? "",
  week_end: row?.week_end ?? "",
  week_label: row?.week_label ?? "",
  new_candidate_screen: toInt(row?.new_candidate_screen),
  consultant_relations_checkin: toInt(row?.consultant_relations_checkin),
  internal_submissions: toInt(row?.internal_submissions),
  client_submissions: toInt(row?.client_submissions),
  client_interviews: toInt(row?.client_interviews),
  interviewing: toInt(row?.interviewing),
  total_placements: toInt(row?.total_placements),
  placement_backfill: toInt(row?.placement_backfill),
  placement_extension: toInt(row?.placement_extension),
  placement_new_headcount: toInt(row?.placement_new_headcount),
  placement_ace: toInt(row?.placement_ace),
});

// Persists a piece of state in localStorage so it survives page refreshes.
// Falls back to defaultValue when nothing is stored or storage is unavailable
// (private browsing, quota exceeded, etc.).
function useStickyState<T>(
  key: string,
  defaultValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored !== null) return JSON.parse(stored) as T;
    } catch {
      /* ignore */
    }
    return defaultValue;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }, [key, value]);

  return [value, setValue];
}

const STORAGE_KEY = "recruiterDashboard";

// ----- Performance Insights (Abby's #8 new metrics) -----
//
// Built on assumptions documented in the backend's
// fetchRecruiterPerformanceExtras() function. Tweak after Abby's feedback.

interface AverageMetric {
  averageDays: number | null;
  jobCount: number;
}
interface WinPercent {
  wonCount: number;
  eligibleCount: number;
  percent: number | null;
}
interface RecruiterRatio {
  recruiter: string;
  ratio: number | null;
  averageDays: number | null;
  [k: string]: any; // numerator/denominator field names vary by metric
}
interface AverageMargin {
  available: boolean;
  reason?: string;
}
interface PerformanceExtras {
  timeToFill: AverageMetric;
  timeToSubmitInternal: AverageMetric;
  timeToSubmitToClient: AverageMetric;
  winPercent: WinPercent;
  interviewToPlacement: RecruiterRatio[];
  submittalToClient: RecruiterRatio[];
  averageMargin: AverageMargin;
  assumptions: string[];
}

const fmtDays = (n: number | null): string =>
  n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(1)} d`;

const fmtPct = (n: number | null): string =>
  n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(1)}%`;

const fmtRatioPct = (r: number | null): string =>
  r == null || !Number.isFinite(r) ? "—" : `${(r * 100).toFixed(1)}%`;

const RecruiterPerformanceReport = () => {
  const [summaryRows, setSummaryRows] = useState<SummaryRow[]>([]);
  const [weeklyRows, setWeeklyRows] = useState<WeeklyRow[]>([]);
  const [headcountRows, setHeadcountRows] = useState<HeadcountRow[]>([]);
  const [extras, setExtras] = useState<PerformanceExtras | null>(null);
  const [loadingExtras, setLoadingExtras] = useState<boolean>(false);
  const [extrasError, setExtrasError] = useState<string | null>(null);

  const [startDate, setStartDate] = useStickyState<string>(
    `${STORAGE_KEY}.startDate`,
    DEFAULT_START_DATE,
  );
  const [endDate, setEndDate] = useStickyState<string>(
    `${STORAGE_KEY}.endDate`,
    todayAsInputValue(),
  );
  const [selectedRecruiter, setSelectedRecruiter] = useStickyState<string>(
    `${STORAGE_KEY}.recruiter`,
    "all",
  );

  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [drilldownOpen, setDrilldownOpen] = useState<boolean>(false);
  const [drilldownLoading, setDrilldownLoading] = useState<boolean>(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const [drilldownRows, setDrilldownRows] = useState<DrilldownRow[]>([]);
  const [drilldownMetric, setDrilldownMetric] =
    useState<DrilldownMetric | null>(null);
  const [drilldownRecruiter, setDrilldownRecruiter] = useState<string>("");

  const [headcountModalOpen, setHeadcountModalOpen] = useState<boolean>(false);
  const [headcountModalLoading, setHeadcountModalLoading] =
    useState<boolean>(false);
  const [headcountModalError, setHeadcountModalError] = useState<string | null>(
    null,
  );
  const [headcountModalRows, setHeadcountModalRows] = useState<HeadcountRow[]>(
    [],
  );
  const [headcountModalRecruiter, setHeadcountModalRecruiter] =
    useState<string>("");

  const [totalHeadcountRows, setTotalHeadcountRows] = useState<
    TotalHeadcountRow[]
  >([]);

  const [totalHeadcountLoading, setTotalHeadcountLoading] =
    useState<boolean>(false);

  const [totalHeadcountError, setTotalHeadcountError] = useState<string | null>(
    null,
  );

  const [totalHeadcountModalOpen, setTotalHeadcountModalOpen] =
    useState<boolean>(false);

  const [totalHeadcountModalRecruiter, setTotalHeadcountModalRecruiter] =
    useState<string>("");

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);

    try {
      const params: Record<string, string> = {
        startDate,
        endDate,
      };

      if (selectedRecruiter !== "all") {
        params.recruiter = selectedRecruiter;
      }

      const response = await axios.get("/recruiter-performance-summary", {
        params,
      });

      const rows: SummaryRow[] = Array.isArray(response.data)
        ? response.data.map(normalizeSummaryRow)
        : [];

      setSummaryRows(rows);
    } catch (err: any) {
      console.error("Error fetching recruiter performance summary:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load recruiter performance summary.",
      );
    } finally {
      setLoadingSummary(false);
    }
  }, [startDate, endDate, selectedRecruiter]);

  const fetchTotalHeadcount = useCallback(async () => {
    setTotalHeadcountLoading(true);
    setTotalHeadcountError(null);

    try {
      const response = await axios.get(
        "/recruiter-performance-headcount-total",
      );

      setTotalHeadcountRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error("Error fetching total headcount:", err);

      setTotalHeadcountError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load total headcount.",
      );
    } finally {
      setTotalHeadcountLoading(false);
    }
  }, []);

  const fetchRecruiterDetails = useCallback(async () => {
    if (selectedRecruiter === "all") {
      setWeeklyRows([]);
      setHeadcountRows([]);
      return;
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const weeklyResponse = await axios.get("/recruiter-performance-weekly", {
        params: {
          startDate,
          endDate,
          recruiter: selectedRecruiter,
        },
      });

      const headcountResponse = await axios.get(
        "/recruiter-performance-headcount",
        {
          params: {
            recruiter: selectedRecruiter,
          },
        },
      );

      setWeeklyRows(
        Array.isArray(weeklyResponse.data)
          ? weeklyResponse.data.map(normalizeWeeklyRow)
          : [],
      );
      setHeadcountRows(
        Array.isArray(headcountResponse.data) ? headcountResponse.data : [],
      );
    } catch (err: any) {
      console.error("Error fetching recruiter details:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load recruiter details.",
      );
    } finally {
      setLoadingDetails(false);
    }
  }, [startDate, endDate, selectedRecruiter]);

  const fetchExtras = useCallback(async () => {
    setLoadingExtras(true);
    setExtrasError(null);
    try {
      const response = await axios.get("/recruiter-performance-extras", {
        params: { startDate, endDate },
      });
      setExtras(response.data as PerformanceExtras);
    } catch (err: any) {
      console.error("Error fetching performance insights:", err);
      setExtrasError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load performance insights.",
      );
    } finally {
      setLoadingExtras(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchExtras();
  }, [fetchExtras]);

  useEffect(() => {
    fetchRecruiterDetails();
  }, [fetchRecruiterDetails]);

  useEffect(() => {
    fetchTotalHeadcount();
  }, [fetchTotalHeadcount]);

  const openDrilldown = async (
    recruiter: string,
    metric: DrilldownMetric,
  ): Promise<void> => {
    setDrilldownMetric(metric);
    setDrilldownRecruiter(recruiter);
    setDrilldownRows([]);
    setDrilldownError(null);
    setDrilldownLoading(true);
    setDrilldownOpen(true);

    try {
      const response = await axios.get("/recruiter-performance-drilldown", {
        params: {
          recruiter,
          metric,
          startDate,
          endDate,
        },
      });

      setDrilldownRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error("Error fetching drilldown:", err);
      setDrilldownError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load drilldown data.",
      );
    } finally {
      setDrilldownLoading(false);
    }
  };

  const openHeadcountDrilldown = async (recruiter: string): Promise<void> => {
    setHeadcountModalRecruiter(recruiter);
    setHeadcountModalRows([]);
    setHeadcountModalError(null);
    setHeadcountModalLoading(true);
    setHeadcountModalOpen(true);

    try {
      const params =
        recruiter === "all" || recruiter === "Team Total" ? {} : { recruiter };

      const response = await axios.get("/recruiter-performance-headcount", {
        params,
      });

      setHeadcountModalRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error("Error fetching rolling headcount:", err);

      setHeadcountModalError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load rolling headcount.",
      );
    } finally {
      setHeadcountModalLoading(false);
    }
  };

  const openTotalHeadcountDrilldown = (recruiter: string): void => {
    setTotalHeadcountModalRecruiter(recruiter);
    setTotalHeadcountModalOpen(true);
  };

  const filteredTotalHeadcountRows = useMemo(() => {
    if (
      totalHeadcountModalRecruiter === "Team Total" ||
      totalHeadcountModalRecruiter === "all"
    ) {
      return totalHeadcountRows;
    }

    return totalHeadcountRows.filter(
      (row) => row.recruiter === totalHeadcountModalRecruiter,
    );
  }, [totalHeadcountRows, totalHeadcountModalRecruiter]);

  const renderClickableMetric = (
    recruiter: string,
    metric: DrilldownMetric,
    value: number,
  ) => {
    return (
      <button
        type='button'
        className='btn btn-link p-0 recruiter-metric-link'
        onClick={() => openDrilldown(recruiter, metric)}
      >
        {toInt(value)}
      </button>
    );
  };

  const drilldownShowsNotes =
    drilldownMetric === "new_candidate_screen" ||
    drilldownMetric === "consultant_relations_checkin";

  const getConsultantRelationRowClass = (
    lastConsultantRelation?: string | null,
  ): string => {
    if (!lastConsultantRelation) return "";

    const last = new Date(lastConsultantRelation);
    const now = new Date();

    const weeks = (now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24 * 7);

    return weeks > 8 ? "consultant-relation-overdue" : "";
  };

  const totalHeadcountByRecruiter = useMemo(() => {
    const counts = new Map<string, number>();

    totalHeadcountRows.forEach((row) => {
      if (!row.recruiter) return;

      counts.set(row.recruiter, (counts.get(row.recruiter) || 0) + 1);
    });

    return counts;
  }, [totalHeadcountRows]);

  const summaryRowsWithTotalHeadcount = useMemo<SummaryRow[]>(() => {
    return summaryRows.map((row) => ({
      ...row,
      total_headcount: totalHeadcountByRecruiter.get(row.recruiter) || 0,
    }));
  }, [summaryRows, totalHeadcountByRecruiter]);

  const uniqueRecruiters = useMemo(() => {
    return summaryRowsWithTotalHeadcount.map((row) => row.recruiter).sort();
  }, [summaryRowsWithTotalHeadcount]);

  const selectedSummaryRow = useMemo(() => {
    if (selectedRecruiter === "all") return null;

    return (
      summaryRowsWithTotalHeadcount.find(
        (row) => row.recruiter === selectedRecruiter,
      ) || null
    );
  }, [summaryRowsWithTotalHeadcount, selectedRecruiter]);

  const teamTotals = useMemo<SummaryRow | null>(() => {
    if (!summaryRowsWithTotalHeadcount.length) return null;

    return summaryRowsWithTotalHeadcount.reduce<SummaryRow>(
      (acc, row) => ({
        recruiter: "Team Total",
        new_candidate_screen:
          toInt(acc.new_candidate_screen) + toInt(row.new_candidate_screen),
        consultant_relations_checkin:
          toInt(acc.consultant_relations_checkin) +
          toInt(row.consultant_relations_checkin),
        internal_submissions:
          toInt(acc.internal_submissions) + toInt(row.internal_submissions),
        client_submissions:
          toInt(acc.client_submissions) + toInt(row.client_submissions),
        client_interviews:
          toInt(acc.client_interviews) + toInt(row.client_interviews),
        interviewing: toInt(acc.interviewing) + toInt(row.interviewing),
        total_placements:
          toInt(acc.total_placements) + toInt(row.total_placements),
        placement_backfill:
          toInt(acc.placement_backfill) + toInt(row.placement_backfill),
        placement_extension:
          toInt(acc.placement_extension) + toInt(row.placement_extension),
        placement_new_headcount:
          toInt(acc.placement_new_headcount) +
          toInt(row.placement_new_headcount),
        rolling_headcount:
          toInt(acc.rolling_headcount) + toInt(row.rolling_headcount),
        placement_ace: toInt(acc.placement_ace) + toInt(row.placement_ace),
        total_headcount:
          toInt(acc.total_headcount) + toInt(row.total_headcount),
      }),
      {
        recruiter: "Team Total",
        new_candidate_screen: 0,
        consultant_relations_checkin: 0,
        internal_submissions: 0,
        client_submissions: 0,
        client_interviews: 0,
        interviewing: 0,
        total_placements: 0,
        placement_backfill: 0,
        placement_extension: 0,
        placement_new_headcount: 0,
        rolling_headcount: 0,
        placement_ace: 0,
        total_headcount: 0,
      },
    );
  }, [summaryRowsWithTotalHeadcount]);

  const showTeamTotal = selectedRecruiter === "all" && !!teamTotals;

  return (
    <Col xs='12'>
      <style>{`
        .recruiter-performance-table {
          width: 100%;
          border-collapse: collapse;
          margin: 12px 0 0 0;
          font-size: 0.95rem;
          text-align: left;
        }

        .recruiter-performance-table th,
        .recruiter-performance-table td {
          padding: 10px 12px;
          border: 1px solid #ddd;
          vertical-align: top;
        }

        .recruiter-performance-table th {
          background-color: #f4f4f4;
          font-weight: 600;
          white-space: nowrap;
        }

        .recruiter-performance-table .sticky-head th {
          position: sticky;
          top: 0;
          z-index: 2;
          background-color: #f4f4f4;
        }

        .recruiter-performance-table .summary-total-row td {
          font-weight: 700;
          background: #fafafa;
        }

        .recruiter-metric-link {
          font-weight: 600;
          text-decoration: underline;
        }

        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          align-items: end;
        }

        .table-scroll-wrap {
          overflow-x: auto;
          overflow-y: auto;
          max-height: 500px;
        }

        .compact-note {
          font-size: 0.85rem;
          color: #666;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
        }

        .kpi-box {
          border: 1px solid #e3e3e3;
          border-radius: 8px;
          padding: 12px;
          background: #fff;
        }

        .kpi-label {
          font-size: 0.8rem;
          color: #666;
          margin-bottom: 4px;
        }

        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .muted-empty {
          color: #777;
          margin-bottom: 0;
        }

        .drilldown-meta {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 12px;
        }

        .drilldown-comments {
          max-width: 420px;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .consultant-relation-overdue td {
          background-color: #ffe5e5;
        }

        .consultant-relation-overdue:hover td {
          background-color: #ffd4d4;
        }
      `}</style>

      <Card className='mb-3'>
        <CardHeader>
          <H3 className='font-primary mb-0'>Recruiter Performance Summary</H3>
        </CardHeader>
        <CardBody>
          <div className='filter-grid'>
            <div>
              <Label for='recruiter-start-date'>Start Date</Label>
              <Input
                id='recruiter-start-date'
                type='date'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div>
              <Label for='recruiter-end-date'>End Date</Label>
              <Input
                id='recruiter-end-date'
                type='date'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div>
              <Label for='recruiter-filter'>Recruiter</Label>
              <Input
                id='recruiter-filter'
                type='select'
                value={selectedRecruiter}
                onChange={(e) => setSelectedRecruiter(e.target.value)}
              >
                <option value='all'>All Recruiters</option>
                {uniqueRecruiters.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Input>
            </div>

            <div>
              <Label>&nbsp;</Label>
              <div>
                <Button
                  color='primary'
                  onClick={fetchSummary}
                  disabled={loadingSummary}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className='compact-note mt-3'>
            Date range is applied to summary, weekly history, and drilldowns.
            Rolling headcount reflects current active logic from the backend.
          </div>

          {error && <div className='text-danger mt-3'>Error: {error}</div>}

          {loadingSummary ? (
            <div className='mt-4'>
              <Spinner size='sm' /> Loading recruiter performance...
            </div>
          ) : (
            <div className='table-scroll-wrap mt-3'>
              <table className='recruiter-performance-table'>
                <thead className='sticky-head'>
                  <tr>
                    <th>Recruiter</th>
                    <th>New Candidate Screen</th>
                    <th>Consultant Relations Check In</th>
                    <th>Internal Submissions</th>
                    <th>Client Submissions</th>
                    <th>Interview Scheduled</th>
                    <th>Total Placements</th>

                    <th>Extension</th>
                    <th>ACE</th>

                    <th>Rolling Headcount</th>
                    <th>Total Headcount</th>
                  </tr>
                </thead>
                <tbody>
                  {showTeamTotal && teamTotals && (
                    <tr className='summary-total-row'>
                      <td>{teamTotals.recruiter}</td>
                      <td>{teamTotals.new_candidate_screen}</td>
                      <td>{teamTotals.consultant_relations_checkin}</td>
                      <td>{teamTotals.internal_submissions}</td>
                      <td>{teamTotals.client_submissions}</td>
                      <td>{teamTotals.interviewing}</td>
                      <td>{teamTotals.total_placements}</td>

                      <td>{teamTotals.placement_extension}</td>
                      <td>{teamTotals.placement_ace}</td>
                      <td>{teamTotals.rolling_headcount}</td>
                      <td>{teamTotals.total_headcount}</td>
                    </tr>
                  )}

                  {summaryRowsWithTotalHeadcount.length > 0 ? (
                    summaryRowsWithTotalHeadcount.map((row) => (
                      <tr key={row.recruiter}>
                        <td>{row.recruiter}</td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "new_candidate_screen",
                            row.new_candidate_screen,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "consultant_relations_checkin",
                            row.consultant_relations_checkin,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "internal_submissions",
                            row.internal_submissions,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "client_submissions",
                            row.client_submissions,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "interviewing",
                            row.interviewing,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "placements",
                            row.total_placements,
                          )}
                        </td>

                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "placement_extension",
                            row.placement_extension,
                          )}
                        </td>
                        <td>
                          {renderClickableMetric(
                            row.recruiter,
                            "placement_ace",
                            row.placement_ace,
                          )}
                        </td>

                        <td>
                          <button
                            type='button'
                            className='btn btn-link p-0 recruiter-metric-link'
                            onClick={() =>
                              openHeadcountDrilldown(row.recruiter)
                            }
                          >
                            {row.rolling_headcount}
                          </button>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-link p-0 recruiter-metric-link'
                            onClick={() =>
                              openTotalHeadcountDrilldown(row.recruiter)
                            }
                          >
                            {row.total_headcount}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12}>
                        <p className='muted-empty'>
                          No recruiter performance data found for the selected
                          filters.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ============================================================
          Performance Insights -- Abby's #8 new metrics. Built on
          documented assumptions; tweak after her feedback.
          ============================================================ */}
      <Card className='mb-3'>
        <CardHeader>
          <H3 className='font-primary'>Performance Insights</H3>
          <small className='text-muted'>
            Date range: {startDate} to {endDate}. Built on documented
            assumptions — values may need tweaking once Abby reviews.
          </small>
        </CardHeader>
        <CardBody>
          {loadingExtras ? (
            <div>
              <Spinner size='sm' /> Loading insights…
            </div>
          ) : extrasError ? (
            <div className='text-danger'>{extrasError}</div>
          ) : !extras ? (
            <div>No data.</div>
          ) : (
            <>
              {/* Top-line averages */}
              {/* <Row className='mb-3'>
                <Col md='3' sm='6'>
                  <div className='border rounded p-2 h-100'>
                    <div className='text-muted small'>Time to Fill</div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                      {fmtDays(extras.timeToFill.averageDays)}
                    </div>
                    <div className='text-muted small'>
                      Across {extras.timeToFill.jobCount} placed job(s).
                      Validated → Placed.
                    </div>
                  </div>
                </Col>
                <Col md='3' sm='6'>
                  <div className='border rounded p-2 h-100'>
                    <div className='text-muted small'>
                      Time to Submit Internally
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                      {fmtDays(extras.timeToSubmitInternal.averageDays)}
                    </div>
                    <div className='text-muted small'>
                      Across {extras.timeToSubmitInternal.jobCount} job(s).
                      Validated → first submission.
                    </div>
                  </div>
                </Col>
                <Col md='3' sm='6'>
                  <div className='border rounded p-2 h-100'>
                    <div className='text-muted small'>
                      Time to Submit to Client
                    </div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                      {fmtDays(extras.timeToSubmitToClient.averageDays)}
                    </div>
                    <div className='text-muted small'>
                      Across {extras.timeToSubmitToClient.jobCount} job(s).
                      Validated → first Client Submission.
                    </div>
                  </div>
                </Col>
                <Col md='3' sm='6'>
                  <div className='border rounded p-2 h-100'>
                    <div className='text-muted small'>Win %</div>
                    <div style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                      {fmtPct(extras.winPercent.percent)}
                    </div>
                    <div className='text-muted small'>
                      {extras.winPercent.wonCount} won /{" "}
                      {extras.winPercent.eligibleCount} non-archived.
                    </div>
                  </div>
                </Col>
              </Row> */}

              {/* Per-recruiter ratios */}
              <Row>
                <Col lg='6'>
                  <h5>Submittal → Client Submittal (per recruiter)</h5>
                  <table className='recruiter-performance-table'>
                    <thead>
                      <tr>
                        <th>Recruiter</th>
                        <th>Submissions</th>
                        <th>Client Submissions</th>
                        <th>Ratio</th>
                        <th>Avg Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extras.submittalToClient.length === 0 ? (
                        <tr>
                          <td colSpan={5}>No data in range.</td>
                        </tr>
                      ) : (
                        extras.submittalToClient.map((row) => (
                          <tr key={row.recruiter}>
                            <td>{row.recruiter}</td>
                            <td>{row.submissionCount ?? 0}</td>
                            <td>{row.clientSubmissionCount ?? 0}</td>
                            <td>{fmtRatioPct(row.ratio)}</td>
                            <td>{fmtDays(row.averageDays)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Col>
                <Col lg='6'>
                  <h5>Interview → Placement (per recruiter)</h5>
                  <table className='recruiter-performance-table'>
                    <thead>
                      <tr>
                        <th>Recruiter</th>
                        <th>Interviews</th>
                        <th>Placements</th>
                        <th>Ratio</th>
                        <th>Avg Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extras.interviewToPlacement.length === 0 ? (
                        <tr>
                          <td colSpan={5}>No data in range.</td>
                        </tr>
                      ) : (
                        extras.interviewToPlacement.map((row) => (
                          <tr key={row.recruiter}>
                            <td>{row.recruiter}</td>
                            <td>{row.interviewCount ?? 0}</td>
                            <td>{row.placementCount ?? 0}</td>
                            <td>{fmtRatioPct(row.ratio)}</td>
                            <td>{fmtDays(row.averageDays)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Col>
              </Row>

              {/* Average Margin -- placeholder; needs billRate data */}
              <Row className='mt-3'>
                <Col xs='12'>
                  <div className='border rounded p-2'>
                    <strong>Average Margin: </strong>
                    <em className='text-muted'>
                      Not available — {extras.averageMargin.reason}
                    </em>
                  </div>
                </Col>
              </Row>

              {/* Assumptions, shown small for visibility */}
              <Row className='mt-3'>
                <Col xs='12'>
                  <details>
                    <summary className='text-muted small'>
                      Assumptions used (click to expand)
                    </summary>
                    <ul className='text-muted small mb-0'>
                      {extras.assumptions.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </details>
                </Col>
              </Row>
            </>
          )}
        </CardBody>
      </Card>

      {selectedRecruiter !== "all" && selectedSummaryRow && (
        <Row>
          <Col xs='12'>
            <Card className='mb-3'>
              <CardHeader>
                <H3 className='mb-0'>{selectedRecruiter}</H3>
              </CardHeader>
              <CardBody>
                <div className='kpi-grid'>
                  <div className='kpi-box'>
                    <div className='kpi-label'>New Candidate Screen</div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.new_candidate_screen}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>
                      Consultant Relations Check In
                    </div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.consultant_relations_checkin}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Internal Submissions</div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.internal_submissions}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Client Submissions</div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.client_submissions}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Interview Scheduled</div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.interviewing}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Total Placements</div>
                    <div className='kpi-value'>
                      {selectedSummaryRow.total_placements}
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Rolling Headcount</div>
                    <div className='kpi-value'>
                      <button
                        type='button'
                        className='btn btn-link p-0 recruiter-metric-link'
                        onClick={() =>
                          openHeadcountDrilldown(selectedRecruiter)
                        }
                      >
                        {selectedSummaryRow.rolling_headcount}
                      </button>
                    </div>
                  </div>
                  <div className='kpi-box'>
                    <div className='kpi-label'>Total Headcount</div>

                    <div className='kpi-value'>
                      <button
                        type='button'
                        className='btn btn-link p-0 recruiter-metric-link'
                        onClick={() =>
                          openTotalHeadcountDrilldown(selectedRecruiter)
                        }
                      >
                        {selectedSummaryRow.total_headcount}
                      </button>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {selectedRecruiter !== "all" && (
        <Row>
          <Col xs='12' lg='7'>
            <Card className='mb-3 shadow-sm'>
              <CardHeader className='bg-light'>
                <H3 className='mb-0'>Weekly Activity History</H3>
                <small>{selectedRecruiter}</small>
              </CardHeader>
              <CardBody>
                {loadingDetails ? (
                  <div>
                    <Spinner size='sm' /> Loading weekly history...
                  </div>
                ) : (
                  <div className='table-scroll-wrap'>
                    <table className='recruiter-performance-table'>
                      <thead className='sticky-head'>
                        <tr>
                          <th>Week</th>
                          <th>New Candidate Screen</th>
                          <th>CR Check In</th>
                          <th>Internal Submissions</th>
                          <th>Client Submissions</th>
                          <th>Interview Scheduled</th>
                          <th>Total Placements</th>

                          <th>Extension</th>
                          <th>ACE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyRows.length > 0 ? (
                          weeklyRows.map((row, idx) => (
                            <tr key={`${row.week_label}-${idx}`}>
                              <td>{row.week_label}</td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "new_candidate_screen",
                                  row.new_candidate_screen,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "consultant_relations_checkin",
                                  row.consultant_relations_checkin,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "internal_submissions",
                                  row.internal_submissions,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "client_submissions",
                                  row.client_submissions,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "interviewing",
                                  row.interviewing,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "placements",
                                  row.total_placements,
                                )}
                              </td>

                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "placement_extension",
                                  row.placement_extension,
                                )}
                              </td>
                              <td>
                                {renderClickableMetric(
                                  row.recruiter,
                                  "placement_ace",
                                  row.placement_ace,
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={10}>
                              <p className='muted-empty'>
                                No weekly data found for the selected recruiter.
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col xs='12' lg='5'>
            <Card className='mb-3 shadow-sm'>
              <CardHeader className='bg-light'>
                <H3 className='mb-0'>Rolling Headcount</H3>
                <small>
                  Placements after 3/1/2025 with timesheets in the current or
                  previous month and an active end date.
                </small>
              </CardHeader>
              <CardBody>
                {loadingDetails ? (
                  <div>
                    <Spinner size='sm' /> Loading headcount...
                  </div>
                ) : (
                  <div className='table-scroll-wrap'>
                    <table className='recruiter-performance-table'>
                      <thead className='sticky-head'>
                        <tr>
                          <th>Name</th>
                          <th>Client</th>
                          <th>Placement ID</th>
                          <th>Job Title</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Last Timesheet Month</th>
                        </tr>
                      </thead>
                      <tbody>
                        {headcountRows.length > 0 ? (
                          headcountRows.map((row) => (
                            <tr key={`${row.placement_id}`}>
                              <td>
                                {row.first_name} {row.last_name}
                              </td>
                              <td>{row.client_name || "—"}</td>
                              <td>{row.placement_id}</td>
                              <td>{row.job_title || "—"}</td>
                              <td>{formatDate(row.startDate)}</td>
                              <td>{formatDate(row.endDate)}</td>
                              <td>{formatDate(row.last_timesheet_month)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7}>
                              <p className='muted-empty'>
                                No headcount rows found for this recruiter.
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        isOpen={drilldownOpen}
        toggle={() => setDrilldownOpen(false)}
        size='xl'
      >
        <ModalHeader toggle={() => setDrilldownOpen(false)}>
          {drilldownMetric
            ? `${formatMetricLabel(drilldownMetric)} Drilldown`
            : "Drilldown"}
        </ModalHeader>
        <ModalBody>
          <div className='drilldown-meta'>
            Recruiter: <strong>{drilldownRecruiter || "—"}</strong> | Range:{" "}
            <strong>
              {formatDate(startDate)} - {formatDate(endDate)}
            </strong>
          </div>

          {drilldownError && (
            <div className='text-danger mb-3'>Error: {drilldownError}</div>
          )}

          {drilldownLoading ? (
            <div>
              <Spinner size='sm' /> Loading drilldown...
            </div>
          ) : (
            <div className='table-scroll-wrap'>
              <table className='recruiter-performance-table'>
                <thead className='sticky-head'>
                  <tr>
                    <th>Date</th>
                    <th>Candidate</th>
                    <th>Client</th>
                    <th>Job Title</th>
                    {drilldownShowsNotes ? (
                      <th>Note ID</th>
                    ) : (
                      <th>Submission ID</th>
                    )}
                    {!drilldownShowsNotes && <th>Placement ID</th>}
                    {drilldownShowsNotes && <th>Action</th>}
                    <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    {drilldownShowsNotes && <th>Comments</th>}
                  </tr>
                </thead>
                <tbody>
                  {drilldownRows.length > 0 ? (
                    drilldownRows.map((row, idx) => (
                      <tr
                        key={`${
                          row.note_id ||
                          row.submission_id ||
                          row.placement_id ||
                          idx
                        }`}
                      >
                        <td>{formatDate(row.event_date)}</td>
                        <td>
                          {row.first_name || ""} {row.last_name || ""}
                        </td>
                        <td>{row.client_name || "—"}</td>
                        <td>{row.job_title || "—"}</td>
                        {drilldownShowsNotes ? (
                          <td>{row.note_id || "—"}</td>
                        ) : (
                          <td>{row.submission_id || "—"}</td>
                        )}
                        {!drilldownShowsNotes && (
                          <td>{row.placement_id || "—"}</td>
                        )}
                        {drilldownShowsNotes && <td>{row.action || "—"}</td>}
                        <td>{row.status || "—"}</td>
                        <td>{formatDate(row.startDate)}</td>
                        <td>{formatDate(row.endDate)}</td>
                        {drilldownShowsNotes && (
                          <td className='drilldown-comments'>
                            {row.comments || "—"}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={drilldownShowsNotes ? 10 : 9}>
                        <p className='muted-empty'>
                          No drilldown rows found for this selection.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={headcountModalOpen}
        toggle={() => setHeadcountModalOpen(false)}
        size='xl'
      >
        <ModalHeader toggle={() => setHeadcountModalOpen(false)}>
          Rolling Headcount
        </ModalHeader>

        <ModalBody>
          <div className='drilldown-meta'>
            Recruiter:{" "}
            <strong>
              {headcountModalRecruiter === "Team Total"
                ? "All Recruiters"
                : headcountModalRecruiter || "—"}
            </strong>
          </div>

          <div className='compact-note mb-3'>
            Active non-internal placements that started after March 1, 2025 and
            have positive normal hours in the current or previous calendar
            month.
          </div>

          {headcountModalError && (
            <div className='text-danger mb-3'>Error: {headcountModalError}</div>
          )}

          {headcountModalLoading ? (
            <div>
              <Spinner size='sm' /> Loading rolling headcount...
            </div>
          ) : (
            <div className='table-scroll-wrap'>
              <table className='recruiter-performance-table'>
                <thead className='sticky-head'>
                  <tr>
                    <th>Recruiter</th>
                    <th>Name</th>
                    <th>Client</th>
                    <th>Placement ID</th>
                    <th>Job Title</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Last Timesheet Month</th>
                    <th>Last CR Check-In</th>
                  </tr>
                </thead>

                <tbody>
                  {headcountModalRows.length > 0 ? (
                    headcountModalRows.map((row) => (
                      <tr
                        key={`${row.recruiter}-${row.placement_id}`}
                        className={getConsultantRelationRowClass(
                          row.last_consultant_relation,
                        )}
                      >
                        <td>{row.recruiter}</td>
                        <td>
                          {row.first_name} {row.last_name}
                        </td>
                        <td>{row.client_name || "—"}</td>
                        <td>{row.placement_id}</td>
                        <td>{row.job_title || "—"}</td>
                        <td>{formatDate(row.startDate)}</td>
                        <td>{formatDate(row.endDate)}</td>
                        <td>{formatDate(row.last_timesheet_month)}</td>
                        <td>{formatDate(row.last_consultant_relation)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>
                        <p className='muted-empty'>
                          No rolling headcount rows found.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={totalHeadcountModalOpen}
        toggle={() => setTotalHeadcountModalOpen(false)}
        size='lg'
      >
        <ModalHeader toggle={() => setTotalHeadcountModalOpen(false)}>
          Total Headcount
        </ModalHeader>

        <ModalBody>
          <div className='drilldown-meta'>
            Recruiter:{" "}
            <strong>
              {totalHeadcountModalRecruiter === "Team Total"
                ? "All Recruiters"
                : totalHeadcountModalRecruiter || "—"}
            </strong>
          </div>

          <div className='compact-note mb-3'>
            Total assigned consultants. Rows highlighted red have not had a
            Consultant Relations update in more than eight weeks.
          </div>

          {totalHeadcountError && (
            <div className='text-danger mb-3'>Error: {totalHeadcountError}</div>
          )}

          {totalHeadcountLoading ? (
            <div>
              <Spinner size='sm' /> Loading total headcount...
            </div>
          ) : (
            <div className='table-scroll-wrap'>
              <table className='recruiter-performance-table'>
                <thead className='sticky-head'>
                  <tr>
                    <th>Candidate</th>
                    <th>Last CR Check-In</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredTotalHeadcountRows.length > 0 ? (
                    filteredTotalHeadcountRows.map((row, idx) => (
                      <tr
                        key={`${row.recruiter}-${row.first_name}-${row.last_name}-${idx}`}
                        className={getConsultantRelationRowClass(
                          row.last_consultant_relation,
                        )}
                      >
                        <td>
                          {row.first_name} {row.last_name}
                        </td>

                        <td>{formatDate(row.last_consultant_relation)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3}>
                        <p className='muted-empty'>
                          No total headcount rows found.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </Col>
  );
};

export default RecruiterPerformanceReport;
